import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  DateFilterFn,
  MatCalendar,
  MatDatepicker,
} from '@angular/material/datepicker';
import { Subscription } from 'rxjs';
import { tr } from '@util/tr';

@Component({
  selector: 'ws-datepicker',
  templateUrl: './ws-datepicker.component.html',
  styleUrls: ['./ws-datepicker.component.scss'],
})
export class WsDatepickerComponent implements AfterViewInit, OnDestroy {
  @Input() selectedDate: Date | null = null;

  @Input() tooltip: string = tr('Select a date');

  @Input() mode: 'icon' | 'field' = 'icon';

  @Input() placeholder: string = tr('Date');

  @Output() dateSelected: EventEmitter<Date> = new EventEmitter<Date>();

  /** Visible calendar month changed (picker next/prev, year/multi-year drills). Parent may fetch data for coloring. */
  @Output() activeVisibleMonthChange = new EventEmitter<Date>();

  @Input() getClassByDate: (date: Date) => string | undefined = () => undefined;

  @Input() dateFilter: DateFilterFn<Date> = () => true;

  @Input() max: Date | null = null;
  @Input() min: Date | null = null;

  @ViewChild(MatDatepicker) private pickerRef?: MatDatepicker<Date>;

  private pickerOpenedSub: Subscription | undefined;
  private pickerClosedSub: Subscription | undefined;
  private calendarStateSub: Subscription | undefined;
  private lastEmittedYm: string | null = null;
  /** Avoid double-binding opened/closed streams. */
  private pickerOutletBound = false;

  /** Retries binding when ViewChild is not ready yet (e.g. *ngIf). */
  private bindPickerRetries = 0;

  openDatepicker(datepicker: MatDatepicker<Date>): void {
    datepicker.open();
  }

  onDateChange(event: Date): void {
    if (event) {
      this.selectedDate = event;
      this.dateSelected.emit(event);
    }
  }

  ngAfterViewInit(): void {
    // defer so ViewChild(MatDatepicker) exists inside *ngIf branch
    setTimeout(() => this.bindPickerStreams());
  }

  ngOnDestroy(): void {
    this.teardownCalendarTracking();
    this.pickerOpenedSub?.unsubscribe();
    this.pickerClosedSub?.unsubscribe();
    this.pickerOpenedSub = undefined;
    this.pickerClosedSub = undefined;
  }

  private bindPickerStreams(): void {
    if (this.pickerOutletBound) return;
    const picker = this.pickerRef;
    if (!picker) {
      if (this.bindPickerRetries++ < 12) {
        setTimeout(() => this.bindPickerStreams(), 0);
      }
      return;
    }
    this.pickerOutletBound = true;
    this.pickerOpenedSub = picker.openedStream.subscribe(() => {
      setTimeout(() => this.wireCalendarMonthTracking());
    });
    this.pickerClosedSub = picker.closedStream.subscribe(() => {
      this.teardownCalendarTracking();
    });
  }

  private wireCalendarMonthTracking(): void {
    this.teardownCalendarTracking();
    const picker = this.pickerRef as any;
    const calendar = picker?._popupComponentRef?.instance?._calendar as
      | MatCalendar<Date>
      | undefined;
    if (!calendar) return;

    this.emitIfNewMonth(new Date(calendar.activeDate));

    this.calendarStateSub = calendar.stateChanges.subscribe(() =>
      this.emitIfNewMonth(new Date(calendar.activeDate)),
    );
  }

  /** Emit canonical first-of-month when YYYY-MM of active view changes */
  private emitIfNewMonth(d: Date): void {
    if (!d || isNaN(d.getTime())) return;
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (ym === this.lastEmittedYm) return;
    this.lastEmittedYm = ym;
    this.activeVisibleMonthChange.emit(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  private teardownCalendarTracking(): void {
    this.calendarStateSub?.unsubscribe();
    this.calendarStateSub = undefined;
    this.lastEmittedYm = null;
  }
}
